import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiList, FiTrophy, FiChevronDown, FiChevronUp } = FiIcons;

const MobileGameInfo = ({ 
  mode, 
  moveHistory, 
  capturedPieces, 
  isComputerThinking,
  gameStats 
}) => {
  const [expandedSection, setExpandedSection] = useState('moves');

  const getPieceSymbol = (piece) => {
    const symbols = {
      white: {
        king: '♔', queen: '♕', rook: '♖',
        bishop: '♗', knight: '♘', pawn: '♙'
      },
      black: {
        king: '♚', queen: '♛', rook: '♜',
        bishop: '♝', knight: '♞', pawn: '♟'
      }
    };
    return symbols[piece.color][piece.type] || '';
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const sections = [
    {
      id: 'moves',
      title: 'Move History',
      icon: FiList,
      content: (
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {moveHistory.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-4">No moves yet</p>
          ) : (
            moveHistory.slice(-8).map((move, index) => {
              const moveNumber = moveHistory.length - 8 + index + 1;
              const fromSquare = String.fromCharCode(97 + move.from[1]) + (8 - move.from[0]);
              const toSquare = String.fromCharCode(97 + move.to[1]) + (8 - move.to[0]);

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-3 p-2 bg-white/5 rounded-lg"
                >
                  <span className="text-gray-500 text-sm w-8">{moveNumber}.</span>
                  <span className="text-xl">{getPieceSymbol(move.piece)}</span>
                  <span className="text-white text-sm font-mono">
                    {fromSquare} → {toSquare}
                  </span>
                  {move.captured && (
                    <span className="text-red-400 text-lg">
                      ×{getPieceSymbol(move.captured)}
                    </span>
                  )}
                </motion.div>
              );
            })
          )}
        </div>
      )
    },
    {
      id: 'captures',
      title: 'Captured Pieces',
      icon: FiTrophy,
      content: (
        <div className="space-y-4">
          <div>
            <p className="text-gray-300 text-sm mb-2 font-medium">White captured:</p>
            <div className="flex flex-wrap gap-1 min-h-[32px] p-2 bg-white/5 rounded-lg">
              {capturedPieces.white.length === 0 ? (
                <span className="text-gray-500 text-sm">None</span>
              ) : (
                capturedPieces.white.map((piece, index) => (
                  <motion.span
                    key={index}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="text-xl"
                  >
                    {getPieceSymbol(piece)}
                  </motion.span>
                ))
              )}
            </div>
          </div>
          
          <div>
            <p className="text-gray-300 text-sm mb-2 font-medium">Black captured:</p>
            <div className="flex flex-wrap gap-1 min-h-[32px] p-2 bg-white/5 rounded-lg">
              {capturedPieces.black.length === 0 ? (
                <span className="text-gray-500 text-sm">None</span>
              ) : (
                capturedPieces.black.map((piece, index) => (
                  <motion.span
                    key={index}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="text-xl"
                  >
                    {getPieceSymbol(piece)}
                  </motion.span>
                ))
              )}
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-3">
      {/* Computer Thinking Indicator */}
      {isComputerThinking && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-3"
        >
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-300"></div>
            <p className="text-blue-300 font-medium">Computer is thinking...</p>
          </div>
        </motion.div>
      )}

      {/* Expandable Sections */}
      {sections.map((section) => (
        <motion.div
          key={section.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 overflow-hidden"
        >
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={() => toggleSection(section.id)}
            className="w-full flex items-center justify-between p-4 text-left"
          >
            <div className="flex items-center gap-3">
              <SafeIcon icon={section.icon} className="text-blue-400" />
              <span className="text-white font-semibold">{section.title}</span>
            </div>
            <SafeIcon 
              icon={expandedSection === section.id ? FiChevronUp : FiChevronDown} 
              className="text-gray-400"
            />
          </motion.button>
          
          <AnimatePresence>
            {expandedSection === section.id && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="border-t border-white/10"
              >
                <div className="p-4">
                  {section.content}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ))}

      {/* Game Stats Summary */}
      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
        <h3 className="text-white font-semibold mb-3">Game Stats</h3>
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center">
            <div className="text-lg font-bold text-white">{gameStats?.totalMoves || 0}</div>
            <div className="text-xs text-gray-400">Moves</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-yellow-400">{gameStats?.checksDelivered || 0}</div>
            <div className="text-xs text-gray-400">Checks</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-purple-400">{gameStats?.piecesCaptures || 0}</div>
            <div className="text-xs text-gray-400">Captures</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileGameInfo;